const qs = (selector, root = document) => root.querySelector(selector);
const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));
const THEME_I18N = window.MaisonLune?.i18n || {};
const themeText = (key, fallback = '') => THEME_I18N[key] || fallback;
const themeTextWith = (key, fallback, replacements = {}) => Object.entries(replacements).reduce(
  (text, [token, value]) => text.replaceAll(token, String(value)),
  themeText(key, fallback),
);
const localizeOptionName = (name = '') => {
  const key = String(name).trim().toLocaleLowerCase();
  if (key === 'kolor' || key === 'color' || key === 'colour') return themeText('color', name);
  if (key === 'rozmiar' || key === 'size') return themeText('size', name);
  return name;
};
const localizeOptionValue = (value = '') => {
  const key = String(value).trim().toLocaleLowerCase();
  if (key === 'zloty' || key === 'złoty' || key === 'gold') return themeText('gold', value);
  if (key === 'srebrny' || key === 'silver') return themeText('silver', value);
  return value;
};
const WISHLIST_KEY = 'maison-lune-wishlist';
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
const sliderScrollFrames = new WeakSet();
const gallerySnapPointCache = new WeakMap();
let themeInitialized = false;
let cartRequest = null;
let cartStateVersion = 0;
const quickAddProductCache = new Map();
let quickAddState = null;

document.addEventListener('DOMContentLoaded', initializeTheme);

window.addEventListener('pageshow', (event) => {
  if (!themeInitialized) {
    initializeTheme();
    return;
  }

  if (event.persisted) {
    refreshCartDrawer(false);
    updateWishlistUI();
    syncScrollHeaderState();
    initializeProductGallery();
  }
});

function initializeTheme() {
  if (themeInitialized) return;
  themeInitialized = true;
  updateWishlistUI();
  syncScrollHeaderState();
  syncCollectionGridControlsForViewport();
  initializeProductGallery();
  initializeManagedVideos();
  initializeProductMiniVideos();
  initializeStyleWithScrollers();
  initializeStickyAddToCart();
  initializeNewsletterMessages();
  initializeMarketLanguage();
}

function initializeStyleWithScrollers(root = document) {
  qsa('[data-style-with-scroller], [data-cart-upsell-scroller]', root).forEach((scroller) => {
    if (scroller.dataset.styleWithReady === 'true') return;
    scroller.dataset.styleWithReady = 'true';

    let pointerId = null;
    let startX = 0;
    let startScrollLeft = 0;
    let dragged = false;

    scroller.addEventListener('wheel', (event) => {
      if (scroller.scrollWidth <= scroller.clientWidth || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      event.preventDefault();
      scroller.scrollLeft += event.deltaY;
    }, { passive: false });

    scroller.addEventListener('pointerdown', (event) => {
      if (
        event.target.closest('button')
        || event.pointerType === 'touch'
        || (event.pointerType === 'mouse' && event.button !== 0)
      ) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startScrollLeft = scroller.scrollLeft;
      dragged = false;
      scroller.setPointerCapture?.(pointerId);
    });

    scroller.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;
      const distance = event.clientX - startX;
      if (!dragged && Math.abs(distance) < 5) return;
      dragged = true;
      scroller.classList.add('is-dragging');
      scroller.scrollLeft = startScrollLeft - distance;
      event.preventDefault();
    });

    const finishDrag = (event) => {
      if (event.pointerId !== pointerId) return;
      if (scroller.hasPointerCapture?.(pointerId)) scroller.releasePointerCapture(pointerId);
      pointerId = null;
      scroller.classList.remove('is-dragging');
      if (!dragged) return;
      scroller.dataset.styleWithDragged = 'true';
      window.setTimeout(() => delete scroller.dataset.styleWithDragged, 0);
    };

    scroller.addEventListener('pointerup', finishDrag);
    scroller.addEventListener('pointercancel', finishDrag);
    scroller.addEventListener('click', (event) => {
      if (scroller.dataset.styleWithDragged !== 'true') return;
      event.preventDefault();
      event.stopPropagation();
    }, true);
  });
}

document.addEventListener('shopify:section:load', (event) => {
  initializeStyleWithScrollers(event.target);
});

document.addEventListener('change', (event) => {
  const selector = event.target.closest('[data-localization-select]');
  if (!selector) return;
  if (!selector.form) return;
  if (selector.name === 'language_code') {
    try {
      window.localStorage.setItem('maison-lune-language-preference', selector.value);
    } catch (error) {
      // Storage can be unavailable in strict privacy modes; the form still works.
    }
  }
  if (typeof selector.form.requestSubmit === 'function') selector.form.requestSubmit();
  else selector.form.submit();
});

window.addEventListener('storage', (event) => {
  if (event.key === WISHLIST_KEY) updateWishlistUI();
});

let scrollHeaderTicking = false;
window.addEventListener('scroll', () => {
  if (!isHeaderAutoHideTemplate() || scrollHeaderTicking) return;
  scrollHeaderTicking = true;
  window.requestAnimationFrame(() => {
    syncScrollHeaderState();
    scrollHeaderTicking = false;
  });
}, { passive: true });

window.addEventListener('resize', () => {
  if (scrollHeaderTicking) return;
  scrollHeaderTicking = true;
  window.requestAnimationFrame(() => {
    syncScrollHeaderState();
    syncCollectionGridControlsForViewport();
    qsa('[data-product-card-slider]').forEach((slider) => {
      syncProductCardSliderLayout(slider);
    });
    qsa('[data-product-gallery]').forEach((gallery) => {
      gallerySnapPointCache.delete(gallery);
      syncProductGalleryLayout(gallery);
    });
    scrollHeaderTicking = false;
  });
}, { passive: true });

document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-menu-toggle]');
  const close = event.target.closest('[data-menu-close]');
  if (!toggle && !close) return;

  const drawer = qs('[data-menu-drawer]');
  const header = qs('[data-header]');
  const isOpen = Boolean(toggle) && !drawer.classList.contains('is-open');
  if (isOpen) closeOtherOverlays(drawer);
  drawer.classList.toggle('is-open', isOpen);
  drawer.setAttribute('aria-hidden', String(!isOpen));
  header?.classList.toggle('is-open', isOpen);
  qsa('[data-menu-toggle]').forEach((button) => {
    button.setAttribute('aria-expanded', String(isOpen));
  });
  syncOverlayLock();
});

document.addEventListener('click', (event) => {
  const open = event.target.closest('[data-search-open]');
  const close = event.target.closest('[data-search-close]');
  if (!open && !close) return;

  if (open) event.preventDefault();
  const drawer = qs('[data-search-drawer]');
  const isOpen = Boolean(open);
  if (isOpen) closeOtherOverlays(drawer);
  drawer?.classList.toggle('is-open', isOpen);
  drawer?.setAttribute('aria-hidden', String(!isOpen));

  if (isOpen) {
    window.setTimeout(() => qs('[data-search-input]', drawer)?.focus(), 80);
  }
  syncOverlayLock();
});

document.addEventListener('keydown', (event) => {
  const zoomModal = qs('[data-product-zoom]');
  if (zoomModal?.classList.contains('is-open') && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
    event.preventDefault();
    const current = Number(zoomModal.dataset.zoomIndex || 0);
    setProductZoomImage(event.key === 'ArrowRight' ? current + 1 : current - 1);
    return;
  }

  if (event.key !== 'Escape') return;
  qsa('[data-menu-drawer], [data-search-drawer], [data-filter-drawer], [data-cart-drawer], [data-wishlist-drawer], [data-upsell-drawer], [data-quick-add-drawer], [data-product-zoom]').forEach((overlay) => {
    overlay.classList.remove('is-open', 'is-magnified');
    overlay.setAttribute('aria-hidden', 'true');
  });
  document.body.classList.remove('product-zoom-open');
  qsa('[data-menu-toggle]').forEach((button) => {
    button.setAttribute('aria-expanded', 'false');
  });
  qs('[data-header]')?.classList.remove('is-open');
  syncOverlayLock();
});

document.addEventListener('click', (event) => {
  if (!event.target.closest('[data-announcement-close]')) return;
  const announcement = qs('[data-announcement]');
  document.body.classList.add('announcement-hidden');
  if (!announcement) return;
  announcement.classList.add('is-closing');
  window.setTimeout(() => announcement.remove(), 300);
});

document.addEventListener('click', (event) => {
  const open = event.target.closest('[data-filter-open]');
  const close = event.target.closest('[data-filter-close]');
  if (!open && !close) return;
  const drawer = qs('[data-filter-drawer]');
  const isOpen = Boolean(open);
  if (isOpen) closeOtherOverlays(drawer);
  drawer.classList.toggle('is-open', isOpen);
  drawer.setAttribute('aria-hidden', String(!isOpen));
  syncOverlayLock();
});

document.addEventListener('click', (event) => {
  const view = event.target.closest('[data-grid-view]');
  if (!view) return;
  const grid = qs('[data-collection-grid]');
  if (!grid) return;
  qsa('[data-grid-view]').forEach((button) => button.setAttribute('aria-pressed', 'false'));
  view.setAttribute('aria-pressed', 'true');
  grid.classList.toggle('product-grid--flat', view.dataset.gridView === 'product');
  if (view.dataset.gridView !== 'product') {
    grid.style.setProperty('--columns', view.dataset.gridView);
  }
  const desktopSize = view.dataset.gridView === 'product'
    ? '12.5vw'
    : `${100 / Math.max(1, Number(view.dataset.gridView || 4))}vw`;
  const mobileSize = view.dataset.gridView === 'product' ? '33.34vw' : '50vw';
  qsa('.product-card img', grid).forEach((image) => {
    image.sizes = `(max-width: 990px) ${mobileSize}, ${desktopSize}`;
  });
});

function syncCollectionGridControlsForViewport() {
  const grid = qs('[data-collection-grid]');
  if (!grid || !isMobileViewport()) return;
  const activeHiddenButton = qs('[data-grid-view][aria-pressed="true"][data-mobile-hidden]');
  if (!activeHiddenButton) return;
  qsa('[data-grid-view]').forEach((button) => button.setAttribute('aria-pressed', 'false'));
  qs('[data-grid-view="2"]')?.setAttribute('aria-pressed', 'true');
  grid.classList.remove('product-grid--flat');
}

document.addEventListener('click', (event) => {
  const summary = event.target.closest('[data-accordion] summary');
  if (!summary) return;

  const details = summary.closest('details');
  if (!details) return;

  event.preventDefault();
  toggleAccordion(details);
});

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-product-card-slide]');
  if (!button) return;
  event.preventDefault();
  event.stopPropagation();

  const slider = button.closest('.product-card__media-wrap')?.querySelector('[data-product-card-slider]');
  if (!slider) return;

  const count = Number(slider.dataset.slideCount || 1);
  const current = Number(slider.dataset.currentSlide || 0);
  const direction = button.dataset.productCardSlide;
  const next = direction === 'next'
    ? Math.min(current + 1, count - 1)
    : Math.max(current - 1, 0);

  setProductCardSlide(slider, next);
});

document.addEventListener('click', (event) => {
  const swatch = event.target.closest('[data-card-swatch]');
  if (!swatch) return;
  event.preventDefault();
  event.stopPropagation();

  const card = swatch.closest('[data-product-card]');
  if (!card) return;
  qsa('[data-card-swatch]', card).forEach((button) => {
    const active = button === swatch;
    button.classList.toggle('is-selected', active);
    button.setAttribute('aria-pressed', String(active));
  });

  if (swatch.dataset.variantId) {
    card.dataset.selectedVariantId = swatch.dataset.variantId;
    qs('[data-quick-add-open]', card)?.setAttribute('data-selected-variant-id', swatch.dataset.variantId);
  }

  if (swatch.dataset.variantImage) {
    updateProductCardVariantImage(card, swatch.dataset.variantImage, swatch.dataset.colorValue);
  }

  pulseButton(swatch);
});

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-product-gallery-slide]');
  if (!button) return;
  event.preventDefault();

  const gallery = qs('[data-product-gallery]');
  if (!gallery) return;

  const count = Number(gallery.dataset.slideCount || 1);
  const current = Number(gallery.dataset.currentSlide || 0);
  const direction = button.dataset.productGallerySlide;
  const next = direction === 'next'
    ? Math.min(current + 1, count - 1)
    : Math.max(current - 1, 0);

  setProductGallerySlide(gallery, next);
});

document.addEventListener('click', (event) => {
  const open = event.target.closest('[data-product-zoom-open]');
  const close = event.target.closest('[data-product-zoom-close]');
  const slideButton = event.target.closest('[data-product-zoom-slide]');

  if (open) {
    event.preventDefault();
    openProductZoom();
    return;
  }

  if (close) {
    event.preventDefault();
    closeProductZoom();
    return;
  }

  if (slideButton) {
    event.preventDefault();
    const modal = slideButton.closest('[data-product-zoom]');
    const current = Number(modal?.dataset.zoomIndex || 0);
    const next = slideButton.dataset.productZoomSlide === 'next' ? current + 1 : current - 1;
    setProductZoomImage(next);
  }
});

document.addEventListener('click', (event) => {
  const viewport = event.target.closest('[data-product-zoom-viewport]');
  if (!viewport) return;

  const modal = viewport.closest('[data-product-zoom]');
  const image = qs('[data-product-zoom-image]', modal);
  if (!modal || !image || !image.getAttribute('src')) return;

  const rect = image.getBoundingClientRect();
  const x = rect.width > 0 ? ((event.clientX - rect.left) / rect.width) * 100 : 50;
  const y = rect.height > 0 ? ((event.clientY - rect.top) / rect.height) * 100 : 50;
  image.style.setProperty('--zoom-x', `${Math.max(0, Math.min(100, x))}%`);
  image.style.setProperty('--zoom-y', `${Math.max(0, Math.min(100, y))}%`);
  modal.classList.toggle('is-magnified');
});

document.addEventListener('pointerdown', (event) => {
  const slider = event.target.closest('[data-product-card-slider]');
  if (!slider || !isMobileViewport()) return;
  slider.dataset.pointerStartX = String(event.clientX);
  slider.dataset.pointerStartY = String(event.clientY);
});

document.addEventListener('pointerdown', (event) => {
  const gallery = event.target.closest('[data-product-gallery]');
  if (
    !gallery
    || event.pointerType === 'touch'
    || Number(gallery.dataset.slideCount || 1) <= 1
    || event.button > 0
  ) return;

  gallery.dataset.pointerActive = 'true';
  gallery.dataset.pointerId = String(event.pointerId);
  gallery.dataset.pointerStartX = String(event.clientX);
  gallery.dataset.pointerStartY = String(event.clientY);
  gallery.dataset.pointerScrollLeft = String(gallery.scrollLeft);
  gallery.dataset.wasDragged = 'false';
  gallery.setPointerCapture?.(event.pointerId);
});

document.addEventListener('pointermove', (event) => {
  const gallery = event.target.closest('[data-product-gallery]');
  if (
    !gallery
    || event.pointerType === 'touch'
    || gallery.dataset.pointerActive !== 'true'
    || Number(gallery.dataset.pointerId) !== event.pointerId
  ) return;

  const startX = Number(gallery.dataset.pointerStartX || event.clientX);
  const startY = Number(gallery.dataset.pointerStartY || event.clientY);
  const startScrollLeft = Number(gallery.dataset.pointerScrollLeft || gallery.scrollLeft);
  const deltaX = event.clientX - startX;
  const deltaY = event.clientY - startY;

  if (Math.abs(deltaX) <= 6 || Math.abs(deltaX) <= Math.abs(deltaY)) return;

  event.preventDefault();
  gallery.classList.add('is-dragging');
  gallery.dataset.wasDragged = 'true';
  gallery.scrollLeft = startScrollLeft - deltaX;
});

document.addEventListener('pointerup', (event) => {
  const gallery = event.target.closest('[data-product-gallery]');
  if (
    !gallery
    || event.pointerType === 'touch'
    || gallery.dataset.pointerActive !== 'true'
    || Number(gallery.dataset.pointerId) !== event.pointerId
  ) return;

  endProductGalleryDrag(gallery, event.pointerId);
});

document.addEventListener('pointercancel', (event) => {
  const gallery = event.target.closest('[data-product-gallery]');
  if (
    !gallery
    || event.pointerType === 'touch'
    || gallery.dataset.pointerActive !== 'true'
    || Number(gallery.dataset.pointerId) !== event.pointerId
  ) return;

  endProductGalleryDrag(gallery, event.pointerId);
});

document.addEventListener('pointerup', (event) => {
  const slider = event.target.closest('[data-product-card-slider]');
  if (!slider || !isMobileViewport()) return;

  const startX = Number(slider.dataset.pointerStartX || event.clientX);
  const startY = Number(slider.dataset.pointerStartY || event.clientY);
  const movedX = Math.abs(event.clientX - startX);
  const movedY = Math.abs(event.clientY - startY);

  if (movedX > 14 && movedX > movedY) {
    slider.dataset.wasSwiped = 'true';
    window.setTimeout(() => {
      delete slider.dataset.wasSwiped;
    }, 220);
  }

  delete slider.dataset.pointerStartX;
  delete slider.dataset.pointerStartY;
});

document.addEventListener('click', (event) => {
  const media = event.target.closest('.product-card__media');
  const slider = media?.querySelector('[data-product-card-slider]');
  if (!slider || slider.dataset.wasSwiped !== 'true') return;
  event.preventDefault();
  event.stopPropagation();
});

document.addEventListener('click', (event) => {
  const gallery = event.target.closest('[data-product-gallery]');
  if (!gallery || gallery.dataset.wasDragged !== 'true') return;
  event.preventDefault();
  event.stopPropagation();
  window.setTimeout(() => {
    if (gallery.dataset.wasDragged === 'true') delete gallery.dataset.wasDragged;
  }, 0);
});

document.addEventListener('scroll', (event) => {
  const slider = event.target.closest?.('[data-product-card-slider]');
  const gallery = event.target.closest?.('[data-product-gallery]');

  if (slider && isMobileViewport()) {
    scheduleScrollFrame(slider, () => {
      const index = Math.round(slider.scrollLeft / Math.max(1, slider.clientWidth));
      updateProductCardSliderControls(slider, index);
    });
  }

  if (gallery) {
    scheduleScrollFrame(gallery, () => {
      const index = getClosestProductGalleryIndex(gallery);
      updateProductGalleryControls(gallery, index);
    });
  }
}, { capture: true, passive: true });

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-quantity]');
  if (!button) return;
  const input = button.parentElement.querySelector('input');
  const direction = button.dataset.quantity;
  const current = Number(input.value || 1);
  input.value = direction === 'plus' ? current + 1 : Math.max(1, current - 1);
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-video-toggle]');
  if (!toggle) return;
  const video = toggle.closest('.hero')?.querySelector('video');
  if (!video) return;
  if (video.paused) {
    video.dataset.userPaused = 'false';
    video.play().catch(() => {});
    toggle.classList.remove('is-paused');
    toggle.setAttribute('aria-pressed', 'false');
    toggle.setAttribute('aria-label', themeText('pauseVideo', 'Pause video'));
  } else {
    video.dataset.userPaused = 'true';
    video.pause();
    toggle.classList.add('is-paused');
    toggle.setAttribute('aria-pressed', 'true');
    toggle.setAttribute('aria-label', themeText('playVideo', 'Play video'));
  }
});

document.addEventListener('click', async (event) => {
  const toggle = event.target.closest('[data-product-mini-video-toggle]');
  if (!toggle) return;

  const card = toggle.closest('[data-product-mini-video]');
  const video = qs('video', card);
  if (!card || !video) return;

  if (!video.paused) {
    video.pause();
    setProductMiniVideoState(card, false);
    return;
  }

  qsa('[data-product-mini-video]').forEach((otherCard) => {
    if (otherCard === card) return;
    qs('video', otherCard)?.pause();
    setProductMiniVideoState(otherCard, false);
  });

  try {
    await video.play();
    setProductMiniVideoState(card, true);
  } catch (error) {
    setProductMiniVideoState(card, false);
  }
});

document.addEventListener('change', (event) => {
  if (!event.target.matches('[data-option-position]')) return;
  const form = event.target.closest('.product-form');
  if (!form) return;

  const group = event.target.closest('.product-option__values');
  qsa('.is-selected', group).forEach((node) => node.classList.remove('is-selected'));
  event.target.closest('label')?.classList.add('is-selected');

  const selectedLabel = qs(`[data-selected-option="${event.target.dataset.optionPosition}"]`, form);
  if (selectedLabel) selectedLabel.textContent = localizeOptionValue(event.target.value);

  const variantsNode = qs('[data-product-variants]', form);
  if (!variantsNode) return;

  let variants = [];
  try {
    variants = JSON.parse(variantsNode.textContent);
  } catch (error) {
    return;
  }

  const selectedOptions = [];
  qsa('[data-option-position]', form).forEach((input) => {
    if (input.checked) {
      selectedOptions[Number(input.dataset.optionPosition) - 1] = input.value;
    }
  });

  const variant = variants.find((item) => {
    const options = item.options || [item.option1, item.option2, item.option3].filter(Boolean);
    return options.every((option, index) => String(option) === String(selectedOptions[index]));
  });

  if (!variant) return;

  const idInput = qs('[data-variant-id]', form);
  const addButton = qs('[data-add-button]', form);
  const priceNode = qs('[data-product-price]');
  if (idInput) idInput.value = variant.id;
  if (addButton) {
    addButton.disabled = !variant.available;
    const addButtonLabel = qs('[data-add-button-label]', addButton);
    if (addButtonLabel) {
      addButtonLabel.textContent = variant.available ? addButton.dataset.addText : addButton.dataset.soldOutText;
    } else {
      addButton.textContent = variant.available ? addButton.dataset.addText : addButton.dataset.soldOutText;
    }
  }
  if (priceNode && typeof variant.price !== 'undefined') {
    const price = Number(variant.price || 0);
    const compareAt = Number(variant.compare_at_price || 0);
    priceNode.innerHTML = compareAt > price
      ? `<span class="price"><span>${escapeHtml(formatMoney(price))}</span><s class="muted">${escapeHtml(formatMoney(compareAt))}</s></span>`
      : `<span class="price">${escapeHtml(formatMoney(price))}</span>`;
  }
  selectProductVariantMedia(form, variant);
  updateStickyAddToCart(variant, selectedOptions);
});

document.addEventListener('click', (event) => {
  const stickyButton = event.target.closest('[data-sticky-add-button]');
  if (!stickyButton) return;
  event.preventDefault();
  const bar = stickyButton.closest('[data-sticky-atc]');
  const formId = bar?.dataset.productFormId;
  const form = (formId && document.getElementById(formId)) || qs('.product-form');
  const mainButton = form ? qs('[data-add-button]', form) : null;
  if (!form || !mainButton || stickyButton.disabled) return;
  if (typeof form.requestSubmit === 'function') form.requestSubmit(mainButton);
  else mainButton.click();
});

document.addEventListener('click', (event) => {
  if (!event.target.closest('[data-cart-open]')) return;
  event.preventDefault();
  openCartDrawer();
});

document.addEventListener('click', (event) => {
  if (!event.target.closest('[data-cart-close]')) return;
  closeCartDrawer();
});

document.addEventListener('click', async (event) => {
  const quantityButton = event.target.closest('[data-cart-quantity]');
  const removeButton = event.target.closest('[data-cart-remove]');
  if (!quantityButton && !removeButton) return;

  const actionButton = quantityButton || removeButton;
  const key = actionButton.dataset.cartKey;
  const quantity = removeButton ? 0 : Number(quantityButton.dataset.cartNextQuantity);
  if (!key || Number.isNaN(quantity)) return;

  setButtonLoading(actionButton, true);
  try {
    await changeCartLine(key, quantity);
  } catch (error) {
    markButtonError(actionButton);
    refreshCartDrawer(false);
  } finally {
    setButtonLoading(actionButton, false);
  }
});

document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-cart-details-toggle]');
  if (!toggle) return;
  const bottom = toggle.closest('.cart-drawer__bottom');
  if (!bottom) return;
  const isOpen = bottom.classList.toggle('is-details-open');
  toggle.setAttribute('aria-expanded', String(isOpen));
});

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('[data-cart-discount-form]');
  if (!form) return;
  event.preventDefault();

  const input = qs('[data-cart-discount-input]', form);
  const button = qs('[data-cart-discount-submit]', form);
  const status = qs('[data-cart-discount-status]', form);
  const code = input?.value.trim() || '';
  if (!code || !button) return;

  if (status) {
    status.textContent = '';
    status.classList.remove('is-success', 'is-error');
  }
  setButtonLoading(button, true);

  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ discount: code }),
    });
    if (!response.ok) throw new Error('Discount update failed');

    const cart = await response.json();
    cartStateVersion += 1;
    cartRequest = null;
    renderCartDrawer(cart);
    updateCartUI(cart, true);

    const applied = (cart.discount_codes || []).some((discountCode) => (
      String(discountCode.code || '').toLowerCase() === code.toLowerCase() && discountCode.applicable
    ));
    if (status) {
      status.textContent = applied
        ? themeText('discountCodeApplied', 'Discount code applied.')
        : themeText('discountCodeInvalid', "This discount code is invalid or doesn't apply to this cart.");
      status.classList.add(applied ? 'is-success' : 'is-error');
    }
    if (applied && input) input.value = '';
  } catch (error) {
    if (status) {
      status.textContent = themeText('discountCodeError', 'The discount could not be applied. Please try again.');
      status.classList.add('is-error');
    }
  } finally {
    setButtonLoading(button, false);
  }
});

document.addEventListener('click', (event) => {
  const open = event.target.closest('[data-wishlist-open]');
  if (!open) return;
  event.preventDefault();
  openWishlistDrawer();
});

document.addEventListener('click', (event) => {
  if (!event.target.closest('[data-wishlist-close]')) return;
  closeWishlistDrawer();
});

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-wishlist-toggle]');
  if (!button) return;
  event.preventDefault();
  toggleWishlistItem(button);
});

document.addEventListener('click', (event) => {
  const remove = event.target.closest('[data-wishlist-remove]');
  if (!remove) return;
  removeWishlistItem(remove.dataset.wishlistRemove);
});

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-quick-add-open]');
  if (!button) return;
  event.preventDefault();
  openQuickAddDrawer(button.dataset.productHandle, button.dataset.selectedVariantId, button.dataset.quickAddLayout);
});

document.addEventListener('click', (event) => {
  if (!event.target.closest('[data-quick-add-close]')) return;
  closeQuickAddDrawer();
});

document.addEventListener('click', (event) => {
  const option = event.target.closest('[data-quick-add-option]');
  if (!option) return;
  event.preventDefault();
  updateQuickAddOption(Number(option.dataset.optionIndex), option.dataset.optionValue);
});

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-quick-add-submit]');
  if (!button) return;
  const variantId = button.dataset.variantId;
  if (!variantId || button.disabled) return;

  setButtonLoading(button, true);
  try {
    await addVariantToCart(variantId, 1);
    markButtonSuccess(button);
    window.setTimeout(closeQuickAddDrawer, 420);
  } catch (error) {
    markButtonError(button);
  } finally {
    setButtonLoading(button, false);
  }
});

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-upsell-open]');
  if (!button) return;
  openUpsellDrawer(button.dataset.productHandle);
});

document.addEventListener('click', (event) => {
  if (!event.target.closest('[data-upsell-close]')) return;
  closeUpsellDrawer();
});

document.addEventListener('change', (event) => {
  if (!event.target.matches('[data-upsell-option]')) return;
  updateUpsellVariant();
});

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-upsell-add]');
  if (!button) return;
  const variantId = button.dataset.variantId;
  if (!variantId) return;
  setButtonLoading(button, true);
  try {
    await addVariantToCart(variantId, 1);
    markButtonSuccess(button);
    window.setTimeout(closeUpsellDrawer, 420);
  } catch (error) {
    markButtonError(button);
  } finally {
    setButtonLoading(button, false);
  }
});

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('form[action*="/cart/add"]');
  if (!form) return;
  event.preventDefault();

  const button = form.querySelector('[type="submit"]');
  const feedbackButtons = form.classList.contains('product-form')
    ? [button, qs('[data-sticky-add-button]')].filter(Boolean)
    : [button].filter(Boolean);
  const formData = new FormData(form);
  feedbackButtons.forEach((feedbackButton) => setButtonLoading(feedbackButton, true));

  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });

    if (!response.ok) throw new Error('Cart add failed');
    await response.json();
    cartStateVersion += 1;
    cartRequest = null;
    try {
      const cart = await fetchRecalculatedCart();
      renderCartDrawer(cart);
      updateCartUI(cart, true);
      openCartDrawer({ refresh: false });
    } catch (refreshError) {
      openCartDrawer();
    }
    feedbackButtons.forEach((feedbackButton) => markButtonSuccess(feedbackButton));
  } catch (error) {
    feedbackButtons.forEach((feedbackButton) => markButtonError(feedbackButton));
    form.submit();
  } finally {
    feedbackButtons.forEach((feedbackButton) => setButtonLoading(feedbackButton, false));
  }
});

function openCartDrawer(options = {}) {
  const { refresh = true } = options;
  const drawer = qs('[data-cart-drawer]');
  const wasOpen = drawer?.classList.contains('is-open');
  closeOtherOverlays(drawer);
  drawer?.classList.add('is-open');
  drawer?.setAttribute('aria-hidden', 'false');
  if (!wasOpen) qs('[data-cart-scroll]', drawer)?.scrollTo({ top: 0 });
  syncOverlayLock();
  if (refresh) refreshCartDrawer(false);
}

function closeCartDrawer() {
  const drawer = qs('[data-cart-drawer]');
  drawer?.classList.remove('is-open');
  drawer?.setAttribute('aria-hidden', 'true');
  syncOverlayLock();
}

async function refreshCartDrawer(animate = false) {
  const requestedVersion = cartStateVersion;
  try {
    const cart = await fetchCart();
    if (requestedVersion !== cartStateVersion) return null;
    renderCartDrawer(cart);
    updateCartUI(cart, animate);
    return cart;
  } catch (error) {
    return null;
  }
}

async function fetchCart() {
  if (cartRequest) return cartRequest;

  const request = fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  }).then((response) => {
    if (!response.ok) throw new Error('Cart fetch failed');
    return response.json();
  }).finally(() => {
    if (cartRequest === request) cartRequest = null;
  });

  cartRequest = request;
  return request;
}

async function fetchRecalculatedCart() {
  const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  });
  if (!response.ok) return fetchCart();
  cartRequest = null;
  return response.json();
}

async function changeCartLine(key, quantity) {
  const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ id: key, quantity }),
  });
  if (!response.ok) throw new Error('Cart change failed');
  const cart = await response.json();
  cartStateVersion += 1;
  cartRequest = null;
  renderCartDrawer(cart);
  updateCartUI(cart, true);
}

async function addVariantToCart(id, quantity = 1) {
  const body = new FormData();
  body.append('id', id);
  body.append('quantity', quantity);
  const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body,
  });
  if (!response.ok) throw new Error('Cart add failed');
  await response.json();
  cartStateVersion += 1;
  cartRequest = null;
  try {
    const cart = await fetchRecalculatedCart();
    renderCartDrawer(cart);
    updateCartUI(cart, true);
    openCartDrawer({ refresh: false });
    return cart;
  } catch (refreshError) {
    openCartDrawer();
    return null;
  }
}

function renderCartDrawer(cart) {
  const target = qs('[data-cart-items]');
  if (!target) return;
  const settings = getCartDrawerSettings();
  if (!cart.items || cart.items.length === 0) {
    target.innerHTML = `<p class="cart-drawer__empty">${escapeHtml(settings.emptyText)}</p>`;
    return;
  }

  target.innerHTML = cart.items.map((item) => {
    const handle = getCartItemHandle(item);
    const itemUrl = item.url || item.product_url || '#';
    const itemImage = item.image || item.featured_image?.url || '';
    const itemTitle = item.product_title || item.title || '';
    item.handle = handle;
    item.url = itemUrl;
    item.image = itemImage;
    item.product_title = itemTitle;
    const originalLinePrice = getCartItemOriginalLinePrice(item);
    const finalLinePrice = getCartItemFinalLinePrice(item);
    const lineDiscount = Math.max(0, originalLinePrice - finalLinePrice, Number(item.line_level_total_discount ?? item.total_discount ?? 0));
    const freeItemCount = getCartItemFreeQuantity(item, originalLinePrice, finalLinePrice);
    const optionLabel = getCartItemOptionLabel(item);
    const discountEntries = getCartItemDiscountEntries(item, lineDiscount);
    const discountHtml = lineDiscount > 0 ? `
      <div class="cart-drawer__line-discounts">
        ${discountEntries.map((discount) => `<span><b>${escapeHtml(discount.title)}</b> &minus;${escapeHtml(formatMoney(discount.amount))}</span>`).join('')}
        ${freeItemCount > 0 ? `<em>${escapeHtml(themeTextWith('freeItemCount', '__COUNT__ free', { __COUNT__: freeItemCount }))}</em>` : ''}
      </div>
    ` : '';
    const priceHtml = `
      <div class="cart-drawer__line-prices">
        ${lineDiscount > 0 ? `<s aria-label="${escapeHtml(themeText('regularPrice', 'Regular price'))}">${escapeHtml(formatMoney(originalLinePrice))}</s>` : ''}
        <strong aria-label="${escapeHtml(themeText('discountedPrice', 'Discounted price'))}">${finalLinePrice === 0 ? escapeHtml(themeText('free', 'Free')) : escapeHtml(formatMoney(finalLinePrice))}</strong>
      </div>
    `;

    return `
    <div class="cart-drawer__item" data-cart-key="${escapeHtml(item.key)}">
      <a href="${escapeHtml(itemUrl)}">
        ${itemImage ? `<img src="${escapeHtml(itemImage)}" alt="${escapeHtml(itemTitle)}" loading="lazy" decoding="async" width="130" height="154">` : ''}
      </a>
      <div class="cart-drawer__item-main">
        <div class="cart-drawer__item-top">
          <a href="${escapeHtml(itemUrl)}">
            <p>${escapeHtml(itemTitle)}</p>
            ${optionLabel ? `<span>${escapeHtml(optionLabel)}</span>` : ''}
            <span class="cart-drawer__quantity-summary">${escapeHtml(themeTextWith('quantitySummary', '__COUNT__ items', { __COUNT__: item.quantity }))}</span>
          </a>
          ${priceHtml}
          ${discountHtml}
        </div>
        <div class="cart-drawer__item-controls">
          <div class="cart-quantity">
            <button type="button" data-cart-quantity data-cart-key="${escapeHtml(item.key)}" data-cart-next-quantity="${Math.max(0, item.quantity - 1)}" aria-label="${escapeHtml(themeText('decreaseQuantity', 'Decrease quantity'))}">-</button>
            <span>${item.quantity}</span>
            <button type="button" data-cart-quantity data-cart-key="${escapeHtml(item.key)}" data-cart-next-quantity="${item.quantity + 1}" aria-label="${escapeHtml(themeText('increaseQuantity', 'Increase quantity'))}">+</button>
          </div>
          <span class="cart-drawer__variant">${escapeHtml(item.variant_title || '')}</span>
          <button class="cart-drawer__line-wish" type="button" data-wishlist-toggle data-product-handle="${escapeHtml(item.handle || '')}" data-product-title="${escapeHtml(item.product_title || '')}" data-product-url="${escapeHtml(item.url || '#')}" data-product-image="${escapeHtml(item.image || '')}" data-product-price="${escapeHtml(formatMoney(item.final_price ?? item.price ?? 0))}" aria-label="${escapeHtml(themeText('addToWishlist', 'Add to wishlist'))}"></button>
          <button class="cart-drawer__line-remove" type="button" data-cart-remove data-cart-key="${escapeHtml(item.key)}" aria-label="${escapeHtml(themeText('removeItem', 'Remove item'))}">x</button>
        </div>
      </div>
    </div>
  `;
  }).join('');
  updateWishlistUI();
}

function getCartItemOriginalLinePrice(item) {
  const quantity = Math.max(0, Number(item.quantity || 0));
  return Number(item.original_line_price ?? ((item.original_price ?? item.price ?? 0) * quantity));
}

function getCartItemFinalLinePrice(item) {
  const quantity = Math.max(0, Number(item.quantity || 0));
  return Number(item.final_line_price ?? item.line_price ?? ((item.final_price ?? item.discounted_price ?? item.price ?? 0) * quantity));
}

function getCartItemDiscountEntries(item, lineDiscount) {
  const allocations = (item.line_level_discount_allocations?.length
    ? item.line_level_discount_allocations
    : item.discount_allocations) || [];
  const source = allocations.length > 0 ? allocations : (item.discounts || []);
  const seen = new Set();
  const entries = source.map((discount) => {
    const application = discount.discount_application || {};
    const title = application.title || discount.title || themeText('discount', 'Discount');
    const amount = Number(discount.amount ?? discount.allocated_amount ?? 0);
    return { title, amount };
  }).filter((discount) => {
    const token = `${discount.title}|${discount.amount}`;
    if (discount.amount <= 0 || seen.has(token)) return false;
    seen.add(token);
    return true;
  });

  if (entries.length === 0 && lineDiscount > 0) {
    entries.push({ title: themeText('discount', 'Discount'), amount: lineDiscount });
  }
  return entries;
}

function getCartItemFreeQuantity(item, originalLinePrice, finalLinePrice) {
  const quantity = Math.max(0, Number(item.quantity || 0));
  if (quantity === 0) return 0;
  if (finalLinePrice === 0 && originalLinePrice > 0) return quantity;

  const originalUnitPrice = Number(item.original_price ?? item.price ?? 0);
  if (originalUnitPrice <= 0) return 0;
  const allocations = (item.line_level_discount_allocations?.length
    ? item.line_level_discount_allocations
    : item.discount_allocations) || [];
  const freeQuantity = allocations.reduce((total, allocation) => {
    const application = allocation.discount_application || {};
    const percentage = Number(application.value ?? 0);
    if (application.value_type !== 'percentage' || percentage < 99.999) return total;
    return total + Math.floor((Number(allocation.amount || 0) / originalUnitPrice) + 0.00001);
  }, 0);
  return Math.min(quantity, freeQuantity);
}

function updateCartUI(cart, animate = false) {
  const settings = getCartDrawerSettings();
  const originalTotal = Number(cart.original_total_price ?? cart.total_price ?? 0);
  const totalPrice = Number(cart.total_price ?? 0);
  const totalDiscount = Math.max(0, Number(cart.total_discount ?? 0), originalTotal - totalPrice);
  qsa('[data-cart-count]').forEach((node) => {
    node.textContent = cart.item_count;
    node.classList.toggle('is-empty', cart.item_count === 0);
    if (animate) {
      node.classList.remove('is-bumping');
      void node.offsetWidth;
      if (cart.item_count > 0) node.classList.add('is-bumping');
    }
  });
  qsa('[data-cart-subtotal]').forEach((node) => { node.textContent = formatMoney(totalPrice); });
  qsa('[data-cart-original-total]').forEach((node) => { node.textContent = formatMoney(originalTotal); });
  qsa('[data-cart-total]').forEach((node) => { node.textContent = formatMoney(totalPrice); });
  qsa('[data-cart-discount-total]').forEach((node) => { node.textContent = `−${formatMoney(totalDiscount)}`; });
  qsa('[data-cart-discount-row]').forEach((node) => { node.hidden = totalDiscount <= 0; });
  qsa('[data-cart-level-discounts]').forEach((node) => {
    const applications = cart.cart_level_discount_applications || [];
    node.innerHTML = applications.map((application) => `<span>${escapeHtml(application.title || themeText('discount', 'Discount'))}</span>`).join('');
    node.hidden = applications.length === 0;
  });
  qsa('[data-cart-item-label]').forEach((node) => { node.textContent = `${cart.item_count} ${settings.itemsSuffix}`; });
  qsa('[data-cart-shipping]').forEach((node) => {
    const reached = settings.freeShippingGoal > 0 && totalPrice >= settings.freeShippingGoal;
    node.textContent = reached ? settings.freeShippingReachedMessage : settings.freeShippingMessage;
  });
  qsa('[data-cart-progress]').forEach((node) => {
    const progress = settings.freeShippingGoal > 0
      ? Math.min(100, Math.round((totalPrice / settings.freeShippingGoal) * 100))
      : 100;
    node.style.width = `${progress}%`;
  });
  syncCartUpsellVisibility(cart);
}

function syncCartUpsellVisibility(cart) {
  const handles = new Set((cart.items || []).map((item) => getCartItemHandle(item)).filter(Boolean));
  const cards = qsa('[data-cart-upsell-card]');
  if (cards.length === 0) return;
  let visibleCount = 0;
  cards.forEach((card) => {
    const hidden = handles.has(card.dataset.productHandle || '');
    card.hidden = hidden;
    if (!hidden) visibleCount += 1;
  });
  const upsellSection = qs('.cart-drawer__upsells');
  if (upsellSection) upsellSection.hidden = visibleCount === 0;
}

function getCartDrawerSettings() {
  const drawer = qs('[data-cart-drawer]');
  return {
    emptyText: drawer?.dataset.emptyText || themeText('cartEmpty', 'Your cart is empty.'),
    itemsSuffix: drawer?.dataset.itemsSuffix || themeText('itemsSuffix', 'ITEM(S)'),
    freeShippingGoal: Number(drawer?.dataset.freeShippingGoal || 0),
    freeShippingMessage: drawer?.dataset.freeShippingMessage || themeText('freeShippingClose', 'You are close to free shipping'),
    freeShippingReachedMessage: drawer?.dataset.freeShippingReachedMessage || themeText('freeShippingReached', 'You have free shipping'),
  };
}

function getCartItemOptionLabel(item) {
  if (item.options_with_values && item.options_with_values.length > 0) {
    return item.options_with_values
      .map((option) => option.value)
      .filter((value) => value && value !== 'Default Title')
      .join(' / ');
  }
  return item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
}

function getCartItemHandle(item) {
  if (item.handle) return item.handle;
  const source = item.url || item.product_url || '';
  const match = String(source).match(/\/products\/([^/?#]+)/);
  if (match) return match[1];
  return String(item.product_id || item.id || '');
}

function getWishlist() {
  try {
    return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
  } catch (error) {
    return [];
  }
}

function saveWishlist(items) {
  try {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
    updateWishlistUI(true);
  } catch (error) {
    updateWishlistUI(false);
  }
}

function toggleWishlistItem(button) {
  const handle = button.dataset.productHandle;
  if (!handle) return;
  const items = getWishlist();
  const existing = items.findIndex((item) => item.handle === handle);

  if (existing >= 0) {
    items.splice(existing, 1);
  } else {
    items.unshift({
      handle,
      title: button.dataset.productTitle || '',
      url: button.dataset.productUrl || '#',
      image: button.dataset.productImage || '',
      price: button.dataset.productPrice || '',
    });
  }

  saveWishlist(items);
  pulseButton(button);
}

function removeWishlistItem(handle) {
  saveWishlist(getWishlist().filter((item) => item.handle !== handle));
}

function updateWishlistUI(animate = false) {
  const items = getWishlist();
  const handles = new Set(items.map((item) => item.handle));

  qsa('[data-wishlist-toggle]').forEach((button) => {
    const active = handles.has(button.dataset.productHandle);
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', String(active));
  });

  qsa('[data-wishlist-count]').forEach((node) => {
    node.textContent = items.length;
    node.classList.toggle('is-empty', items.length === 0);
    if (animate) {
      node.classList.remove('is-bumping');
      void node.offsetWidth;
      if (items.length > 0) node.classList.add('is-bumping');
    }
  });

  renderWishlistDrawer(items);
}

function renderWishlistDrawer(items = getWishlist()) {
  const target = qs('[data-wishlist-items]');
  if (!target) return;
  if (items.length === 0) {
    target.innerHTML = `<p class="wishlist-drawer__empty">${escapeHtml(themeText('wishlistEmpty', 'Your wishlist is empty.'))}</p>`;
    return;
  }

  target.innerHTML = items.map((item) => `
    <div class="wishlist-drawer__item">
      <a href="${escapeHtml(item.url)}">
        ${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy" decoding="async" width="116" height="145">` : ''}
      </a>
      <a href="${escapeHtml(item.url)}">
        <p>${escapeHtml(item.title)}</p>
        <span>${escapeHtml(item.price)}</span>
      </a>
      <button class="wishlist-drawer__remove" type="button" data-wishlist-remove="${escapeHtml(item.handle)}" aria-label="${escapeHtml(themeText('removeFromWishlist', 'Remove from wishlist'))}">x</button>
    </div>
  `).join('');
}

function openWishlistDrawer() {
  updateWishlistUI();
  const drawer = qs('[data-wishlist-drawer]');
  closeOtherOverlays(drawer);
  drawer?.classList.add('is-open');
  drawer?.setAttribute('aria-hidden', 'false');
  syncOverlayLock();
}

function closeWishlistDrawer() {
  const drawer = qs('[data-wishlist-drawer]');
  drawer?.classList.remove('is-open');
  drawer?.setAttribute('aria-hidden', 'true');
  syncOverlayLock();
}

async function openQuickAddDrawer(handle, preferredVariantId = '', layout = 'sheet') {
  if (!handle) return;
  const drawer = qs('[data-quick-add-drawer]');
  const content = qs('[data-quick-add-content]', drawer);
  if (!drawer || !content) return;
  const drawerLayout = layout === 'product' ? 'product' : 'sheet';

  restoreQuickAddFooter(drawer);

  content.innerHTML = `<div class="quick-add-drawer__loading">${escapeHtml(themeText('loading', 'Loading...'))}</div>`;
  drawer.classList.toggle('quick-add-drawer--product', drawerLayout === 'product');
  closeOtherOverlays(drawer);
  drawer.classList.add('is-open');
  drawer.setAttribute('aria-hidden', 'false');
  syncOverlayLock();

  try {
    const product = await fetchQuickAddProduct(handle);
    const variants = product.variants || [];
    const preferred = variants.find((variant) => String(variant.id) === String(preferredVariantId));
    const fallback = variants.find((variant) => variant.available) || variants[0] || null;
    const seedVariant = preferred?.available ? preferred : fallback;
    const options = getQuickAddOptions(product);
    const colorIndex = findColorOptionIndex(options);
    const selectedOptions = options.map((option, index) => {
      const value = getVariantOption(seedVariant, index);
      if (option.values.length <= 1 && option.values[0] === 'Default Title') return option.values[0];
      return index === colorIndex ? value : '';
    });

    quickAddState = { product, options, selectedOptions, colorIndex, layout: drawerLayout };
    renderQuickAddDrawer();
  } catch (error) {
    content.innerHTML = `<div class="quick-add-drawer__loading">${escapeHtml(themeText('productLoadError', 'This product could not be loaded.'))}</div>`;
  }
}

function closeQuickAddDrawer() {
  const drawer = qs('[data-quick-add-drawer]');
  drawer?.classList.remove('is-open');
  drawer?.classList.remove('quick-add-drawer--product');
  drawer?.setAttribute('aria-hidden', 'true');
  quickAddState = null;
  syncOverlayLock();
}

async function fetchQuickAddProduct(handle) {
  if (quickAddProductCache.has(handle)) return quickAddProductCache.get(handle);
  const root = window.Shopify?.routes?.root || '/';
  const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error('Product fetch failed');
  const product = await response.json();
  product.url = product.url || `${root}products/${handle}`;
  quickAddProductCache.set(handle, product);
  return product;
}

function renderQuickAddDrawer() {
  if (!quickAddState) return;
  const drawer = qs('[data-quick-add-drawer]');
  const content = qs('[data-quick-add-content]', drawer);
  restoreQuickAddFooter(drawer);
  const footerButton = qs('[data-quick-add-submit]', drawer);
  const footerLabel = qs('[data-quick-add-submit-label]', drawer);
  if (!drawer || !content || !footerButton || !footerLabel) return;

  const { product, options, selectedOptions, colorIndex, layout } = quickAddState;
  const exactVariant = findQuickAddVariant(product, selectedOptions, true);
  const displayVariant = exactVariant || findQuickAddVariant(product, selectedOptions, false) || (product.variants || [])[0];
  const needsSelection = options.some((option, index) => {
    if (option.values.length <= 1 && option.values[0] === 'Default Title') return false;
    if (index === colorIndex) return false;
    return !selectedOptions[index];
  });
  const selectedColor = colorIndex >= 0 ? selectedOptions[colorIndex] : '';
  const isProductPanel = layout === 'product';
  const productImages = getQuickAddImages(product, displayVariant);
  const productImage = productImages[0] || '';
  const wishlistButton = `
    <button
      class="quick-add-product__wishlist"
      type="button"
      data-wishlist-toggle
      data-product-handle="${escapeHtml(product.handle)}"
      data-product-title="${escapeHtml(product.title)}"
      data-product-url="${escapeHtml(product.url)}"
      data-product-image="${escapeHtml(productImage)}"
      data-product-price="${escapeHtml(formatMoney(displayVariant?.price ?? product.price ?? 0))}"
      aria-label="${escapeHtml(themeText('addToWishlist', 'Add to wishlist'))}"
    >
      ${getHeartIconMarkup()}
    </button>
  `;

  content.innerHTML = `
    <div class="quick-add-product${isProductPanel ? ' quick-add-product--panel' : ''}">
      ${isProductPanel ? `
        <div class="quick-add-product__media">
          <div class="quick-add-product__media-gallery">
            ${productImages.map((image, index) => `
              <div class="quick-add-product__media-item">
                <img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="${index === 0 ? 'eager' : 'lazy'}" decoding="async">
              </div>
            `).join('')}
          </div>
          ${wishlistButton}
        </div>
      ` : ''}
      <div class="quick-add-product__body">
        <div class="quick-add-product__top">
          <div>
            <h2>${escapeHtml(product.title)}</h2>
            <div class="quick-add-product__price">${renderQuickAddPrice(displayVariant, product)}</div>
          </div>
          ${isProductPanel ? '' : wishlistButton}
        </div>
        ${renderQuickAddOptions(product, options, selectedOptions, colorIndex)}
        ${isProductPanel ? `
          <div data-quick-add-footer-slot></div>
          <details class="quick-add-product__details">
            <summary>${escapeHtml(themeText('productDetails', 'Product details'))}</summary>
            <div class="quick-add-product__details-content">${product.description || ''}</div>
          </details>
        ` : ''}
      </div>
    </div>
  `;

  if (isProductPanel) {
    const footerSlot = qs('[data-quick-add-footer-slot]', content);
    const footer = qs('.quick-add-drawer__footer', drawer);
    if (footerSlot && footer) footerSlot.append(footer);
  }

  const canAdd = Boolean(exactVariant && exactVariant.available && !needsSelection);
  footerButton.disabled = !canAdd;
  footerButton.dataset.variantId = canAdd ? exactVariant.id : '';
  footerLabel.textContent = canAdd ? themeText('addToCart', 'Add to cart') : themeText('selectSize', 'Select size');
  updateWishlistUI();
}

function restoreQuickAddFooter(drawer) {
  if (!drawer) return;
  const panel = qs('.quick-add-drawer__panel', drawer);
  const footer = qs('.quick-add-drawer__footer', drawer);
  if (panel && footer && footer.parentElement !== panel) panel.append(footer);
}

function renderQuickAddPrice(variant, product) {
  const price = Number(variant?.price ?? product?.price ?? 0);
  const compareAt = Number(variant?.compare_at_price ?? product?.compare_at_price ?? 0);
  if (compareAt > price) {
    return `
      <span class="price">
        <span>${escapeHtml(formatMoney(price))}</span>
        <s class="muted">${escapeHtml(formatMoney(compareAt))}</s>
      </span>
    `;
  }
  return `<span class="price">${escapeHtml(formatMoney(price))}</span>`;
}

function renderQuickAddOptions(product, options, selectedOptions, colorIndex) {
  return options.map((option, index) => {
    if (option.values.length <= 1 && option.values[0] === 'Default Title') return '';
    const isColor = index === colorIndex;
    const values = option.values || [];
    const buttons = values.map((value) => {
      const active = String(selectedOptions[index]) === String(value);
      const displayValue = localizeOptionValue(value);
      const variant = findQuickAddVariant(product, replaceOptionValue(selectedOptions, index, value), false)
        || findVariantWithOptionValue(product, index, value);
      const disabled = !variant || variant.available === false;
      if (isColor) {
        return `
          <button
            class="quick-add-color${active ? ' is-selected' : ''}"
            type="button"
            data-quick-add-option
            data-option-index="${index}"
            data-option-value="${escapeHtml(value)}"
            aria-pressed="${String(active)}"
            aria-label="${escapeHtml(displayValue)}"
            ${disabled ? 'disabled' : ''}
          >
            <span class="quick-add-color__media">
              ${getColorSwatchImage(value) ? `<img src="${escapeHtml(getColorSwatchImage(value))}" alt="${escapeHtml(displayValue)}" loading="lazy" decoding="async" width="22" height="22">` : `<span>${escapeHtml(String(displayValue).slice(0, 1).toUpperCase())}</span>`}
            </span>
          </button>
        `;
      }

      return `
        <button
          class="quick-add-size${active ? ' is-selected' : ''}"
          type="button"
          data-quick-add-option
          data-option-index="${index}"
          data-option-value="${escapeHtml(value)}"
          aria-pressed="${String(active)}"
          ${disabled ? 'disabled' : ''}
        >
          ${escapeHtml(displayValue)}
        </button>
      `;
    }).join('');

    return `
      <fieldset class="quick-add-option quick-add-option--${isColor ? 'color' : 'size'}">
        <legend>
          <span class="quick-add-option__name">${isColor ? escapeHtml(localizeOptionName(option.name)) : escapeHtml(themeTextWith('selectOption', 'Select __OPTION__', { __OPTION__: localizeOptionName(option.name) }))}</span>
          ${isColor && selectedOptions[index] ? `<span class="quick-add-option__selected">${escapeHtml(localizeOptionValue(selectedOptions[index]))}</span>` : ''}
        </legend>
        <div>${buttons}</div>
      </fieldset>
    `;
  }).join('');
}

function updateQuickAddOption(index, value) {
  if (!quickAddState || Number.isNaN(index)) return;
  quickAddState.selectedOptions[index] = value;
  renderQuickAddDrawer();
}

function updateProductCardVariantImage(card, imageUrl, alt = '') {
  const slider = qs('[data-product-card-slider]', card);
  const image = slider ? qs('.product-card__slide img', slider) : qs('.product-card__media > img', card);
  const media = qs('.product-card__media', card);
  if (!image || !imageUrl) return;

  media?.classList.add('is-image-swapping');
  const applyImage = () => {
    image.src = imageUrl;
    image.removeAttribute('srcset');
    if (alt) image.alt = alt;
    if (slider) setProductCardSlide(slider, 0);
    window.requestAnimationFrame(() => {
      media?.classList.remove('is-image-swapping');
    });
  };

  const preload = new Image();
  preload.onload = applyImage;
  preload.onerror = () => media?.classList.remove('is-image-swapping');
  preload.src = imageUrl;
  if (preload.complete) applyImage();
}

function getQuickAddOptions(product) {
  const productOptions = product.options || [];
  if (productOptions.length === 0) return [];
  return productOptions.map((option, index) => {
    const name = typeof option === 'string' ? option : option.name;
    const values = Array.isArray(option.values)
      ? option.values
      : Array.from(new Set((product.variants || []).map((variant) => getVariantOption(variant, index)).filter(Boolean)));
    return { name: name || themeTextWith('optionNumber', 'Option __NUMBER__', { __NUMBER__: index + 1 }), values };
  });
}

function findColorOptionIndex(options) {
  return options.findIndex((option) => {
    const name = String(option.name || '').toLowerCase();
    return name === 'kolor' || name.includes('color') || name.includes('colour');
  });
}

function getColorSwatchImage(value) {
  const drawer = qs('[data-quick-add-drawer]');
  const key = String(value || '').toLowerCase().trim();
  if ((key.includes('zloty') || key.includes('gold')) && drawer?.dataset.swatchGold) {
    return drawer.dataset.swatchGold;
  }
  if ((key.includes('srebrny') || key.includes('silver')) && drawer?.dataset.swatchSilver) {
    return drawer.dataset.swatchSilver;
  }
  return '';
}

function replaceOptionValue(options, index, value) {
  const next = [...options];
  next[index] = value;
  return next;
}

function findQuickAddVariant(product, selectedOptions, requireFullMatch = true) {
  return (product.variants || []).find((variant) => {
    const options = getVariantOptions(variant);
    return selectedOptions.every((value, index) => {
      if (!value && !requireFullMatch) return true;
      return String(options[index] || '') === String(value || '');
    });
  });
}

function findVariantWithOptionValue(product, index, value) {
  return (product.variants || []).find((variant) => String(getVariantOption(variant, index)) === String(value));
}

function getVariantOption(variant, index) {
  if (!variant) return '';
  const options = getVariantOptions(variant);
  return options[index] || variant[`option${index + 1}`] || '';
}

function getQuickAddImage(product, variant) {
  const variantImage = variant?.featured_image?.src || variant?.featured_image || '';
  if (typeof variantImage === 'string' && variantImage) return variantImage;
  if (product.featured_image) return typeof product.featured_image === 'string' ? product.featured_image : product.featured_image.src || '';
  return (product.images || [])[0] || '';
}

function getQuickAddImages(product, variant) {
  const primaryImage = getQuickAddImage(product, variant);
  const images = [primaryImage, ...(product.images || [])]
    .map((image) => (typeof image === 'string' ? image : image?.src || ''))
    .filter(Boolean);
  return Array.from(new Set(images)).slice(0, 8);
}

function getHeartIconMarkup() {
  return '<svg class="icon icon-heart" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 20.2 4.7 13C2.8 11.1 2.8 8 4.7 6.2c1.8-1.8 4.7-1.7 6.4.2l.9 1 .9-1c1.7-1.9 4.6-2 6.4-.2 1.9 1.8 1.9 4.9 0 6.8L12 20.2Z"></path></svg>';
}

function openUpsellDrawer(handle) {
  const source = qsa('[data-upsell-product-json]').find((node) => node.dataset.upsellProductJson === handle);
  const drawer = qs('[data-upsell-drawer]');
  const content = qs('[data-upsell-content]');
  if (!source || !drawer || !content) return;

  let product;
  try {
    product = JSON.parse(source.textContent);
  } catch (error) {
    return;
  }

  const variant = product.variants.find((item) => item.available) || product.variants[0];
  content.innerHTML = renderUpsellProduct(product, variant);
  closeOtherOverlays(drawer);
  drawer.classList.add('is-open');
  drawer.setAttribute('aria-hidden', 'false');
  syncOverlayLock();
  updateUpsellVariant();
}

function closeUpsellDrawer() {
  const drawer = qs('[data-upsell-drawer]');
  drawer?.classList.remove('is-open');
  drawer?.setAttribute('aria-hidden', 'true');
  syncOverlayLock();
}

function renderUpsellProduct(product, variant) {
  const selectedOptions = getVariantOptions(variant);
  const optionsHtml = (product.options || []).map((option, optionIndex) => {
    const values = option.values || [];
    return `
      <fieldset class="upsell-option">
        <legend>${escapeHtml(localizeOptionName(option.name))}</legend>
        <div>
          ${values.map((value) => `
            <label class="upsell-option__value${String(selectedOptions[optionIndex]) === String(value) ? ' is-selected' : ''}">
              <input type="radio" name="upsell-option-${optionIndex}" value="${escapeHtml(value)}" data-upsell-option data-option-index="${optionIndex}" ${String(selectedOptions[optionIndex]) === String(value) ? 'checked' : ''}>
              <span>${escapeHtml(localizeOptionValue(value))}</span>
            </label>
          `).join('')}
        </div>
      </fieldset>
    `;
  }).join('');

  return `
    <div class="upsell-product" data-upsell-product>
      <div class="upsell-product__media">
        ${product.image ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="lazy" decoding="async" width="520" height="650">` : ''}
      </div>
      <div class="upsell-product__info">
        <h2>${escapeHtml(product.title)}</h2>
        <p data-upsell-price>${formatMoney(variant?.price ?? product.price ?? 0)}</p>
        ${optionsHtml}
        <button class="upsell-product__add" type="button" data-upsell-add data-variant-id="${variant?.id || ''}">
          ${escapeHtml(themeText('addToCart', 'Add to cart'))}
          <span>+</span>
        </button>
        <a class="upsell-product__details" href="${escapeHtml(product.url)}">${escapeHtml(themeText('productDetails', 'Product details'))}</a>
      </div>
      <script type="application/json" data-active-upsell-json>${JSON.stringify(product).replace(/</g, '\\u003c')}</script>
    </div>
  `;
}

function updateUpsellVariant() {
  const productNode = qs('[data-upsell-product]');
  const source = qs('[data-active-upsell-json]', productNode);
  if (!productNode || !source) return;

  let product;
  try {
    product = JSON.parse(source.textContent);
  } catch (error) {
    return;
  }

  const selectedOptions = [];
  qsa('[data-upsell-option]', productNode).forEach((input) => {
    if (input.checked) selectedOptions[Number(input.dataset.optionIndex)] = input.value;
  });

  qsa('.upsell-option__value', productNode).forEach((label) => {
    label.classList.toggle('is-selected', Boolean(qs('input:checked', label)));
  });

  const variant = product.variants.find((item) => {
    const options = getVariantOptions(item);
    return options.every((option, index) => String(option) === String(selectedOptions[index]));
  });

  const addButton = qs('[data-upsell-add]', productNode);
  const price = qs('[data-upsell-price]', productNode);
  if (variant && addButton) {
    addButton.dataset.variantId = variant.id;
    addButton.disabled = !variant.available;
    addButton.firstChild.textContent = variant.available ? themeText('addToCart', 'Add to cart') : themeText('soldOut', 'Sold out');
  }
  if (variant && price) price.textContent = formatMoney(variant.price);
}

function getVariantOptions(variant) {
  return variant?.options || [variant?.option1, variant?.option2, variant?.option3].filter(Boolean);
}

function scheduleScrollFrame(element, callback) {
  if (!element || sliderScrollFrames.has(element)) return;
  sliderScrollFrames.add(element);
  window.requestAnimationFrame(() => {
    sliderScrollFrames.delete(element);
    callback();
  });
}

function toggleAccordion(details) {
  if (!details || details.dataset.animating === 'true') return;
  const content = qs('.accordion__content', details);
  const shouldOpen = !details.open;

  if (!content || REDUCED_MOTION.matches || typeof details.animate !== 'function') {
    details.open = shouldOpen;
    return;
  }

  const startHeight = details.getBoundingClientRect().height;
  details.dataset.animating = 'true';

  if (shouldOpen) details.open = true;

  const contentHeight = content.getBoundingClientRect().height;
  const closedHeight = Math.max(0, details.getBoundingClientRect().height - contentHeight);
  const endHeight = shouldOpen ? details.getBoundingClientRect().height : closedHeight;

  details.style.overflow = 'hidden';
  const animation = details.animate([
    { height: `${startHeight}px` },
    { height: `${endHeight}px` },
  ], {
    duration: 260,
    easing: 'cubic-bezier(.22, .61, .36, 1)',
  });

  content.animate([
    { opacity: shouldOpen ? 0 : 1, transform: shouldOpen ? 'translateY(-4px)' : 'translateY(0)' },
    { opacity: shouldOpen ? 1 : 0, transform: shouldOpen ? 'translateY(0)' : 'translateY(-4px)' },
  ], {
    duration: shouldOpen ? 240 : 180,
    easing: 'ease-out',
  });

  animation.onfinish = () => {
    details.open = shouldOpen;
    details.style.removeProperty('height');
    details.style.removeProperty('overflow');
    delete details.dataset.animating;
  };

  animation.oncancel = () => {
    details.style.removeProperty('height');
    details.style.removeProperty('overflow');
    delete details.dataset.animating;
  };
}

function initializeManagedVideos() {
  const videos = qsa('.hero__media video, .video-snippet__media video');
  if (videos.length === 0) return;

  if (REDUCED_MOTION.matches) {
    videos.forEach((video) => video.pause());
    qsa('[data-video-toggle]').forEach((button) => {
      button.classList.add('is-paused');
      button.setAttribute('aria-pressed', 'true');
      button.setAttribute('aria-label', themeText('playVideo', 'Play video'));
    });
    return;
  }

  if (!('IntersectionObserver' in window)) {
    videos.forEach((video) => {
      if (video.dataset.userPaused !== 'true') video.play().catch(() => {});
    });
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const video = entry.target;
      if (entry.isIntersecting && entry.intersectionRatio >= 0.12 && video.dataset.userPaused !== 'true' && !document.hidden) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }, {
    rootMargin: '120px 0px',
    threshold: [0, 0.12, 0.5],
  });

  videos.forEach((video) => observer.observe(video));

  document.addEventListener('visibilitychange', () => {
    videos.forEach((video) => {
      if (document.hidden) {
        video.pause();
      } else if (video.dataset.userPaused !== 'true') {
        const rect = video.getBoundingClientRect();
        const nearViewport = rect.bottom >= -120 && rect.top <= window.innerHeight + 120;
        if (nearViewport) video.play().catch(() => {});
      }
    });
  });
}

function initializeProductMiniVideos() {
  const cards = qsa('[data-product-mini-video]');
  if (cards.length === 0) return;

  cards.forEach((card) => {
    const video = qs('video', card);
    if (!video) return;
    video.pause();
    setProductMiniVideoState(card, false);
  });

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) return;
        const card = entry.target;
        qs('video', card)?.pause();
        setProductMiniVideoState(card, false);
      });
    }, {
      rootMargin: '80px 0px',
      threshold: 0.05,
    });

    cards.forEach((card) => observer.observe(card));
  }

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) return;
    cards.forEach((card) => {
      qs('video', card)?.pause();
      setProductMiniVideoState(card, false);
    });
  });
}

function initializeNewsletterMessages() {
  qsa('[data-newsletter-message]').forEach((message) => {
    window.setTimeout(() => {
      message.classList.add('is-hidden');
    }, 15000);
  });
}

function setProductMiniVideoState(card, playing) {
  if (!card) return;
  const toggle = qs('[data-product-mini-video-toggle]', card);
  card.classList.toggle('is-playing', playing);
  toggle?.setAttribute('aria-pressed', String(playing));
  toggle?.setAttribute('aria-label', playing ? themeText('pauseVideo', 'Pause video') : themeText('playVideo', 'Play video'));
}

function initializeStickyAddToCart() {
  const bar = qs('[data-sticky-atc]');
  const productSection = qs('.product');
  const formId = bar?.dataset.productFormId;
  const form = (formId && document.getElementById(formId)) || qs('.product-form');
  const mainButton = form ? qs('[data-add-button]', form) : null;
  if (!bar || !productSection || !form || !mainButton) return;

  const variantsNode = qs('[data-product-variants]', form);
  const variantId = Number(qs('[data-variant-id]', form)?.value || 0);
  let selectedVariant = null;
  try {
    const variants = JSON.parse(variantsNode?.textContent || '[]');
    selectedVariant = variants.find((variant) => Number(variant.id) === variantId) || variants[0] || null;
  } catch (error) {
    selectedVariant = null;
  }

  const selectedOptions = [];
  qsa('[data-option-position]', form).forEach((input) => {
    if (input.checked) selectedOptions[Number(input.dataset.optionPosition) - 1] = input.value;
  });
  updateStickyAddToCart(selectedVariant, selectedOptions);

  const setVisibility = (shouldShow) => {
    bar.classList.toggle('is-visible', shouldShow);
    bar.setAttribute('aria-hidden', String(!shouldShow));
  };

  let framePending = false;
  const syncVisibility = () => {
    framePending = false;
    const buttonRect = mainButton.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const visibleTop = Math.max(0, buttonRect.top);
    const visibleBottom = Math.min(viewportHeight, buttonRect.bottom);
    const visibleHeight = Math.max(0, visibleBottom - visibleTop);
    const visibleRatio = buttonRect.height > 0 ? visibleHeight / buttonRect.height : 0;
    const shouldShow = visibleRatio < 0.6;

    setVisibility(shouldShow);
  };

  const scheduleVisibilitySync = () => {
    if (framePending) return;
    framePending = true;
    window.requestAnimationFrame(syncVisibility);
  };

  window.addEventListener('scroll', scheduleVisibilitySync, { passive: true });
  window.addEventListener('resize', scheduleVisibilitySync, { passive: true });
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(([entry]) => {
      const mainButtonIsUsable = entry.isIntersecting && entry.intersectionRatio >= 0.6;
      setVisibility(!mainButtonIsUsable);
    }, { threshold: [0, 0.6, 1] });
    observer.observe(mainButton);
  }
  syncVisibility();
}

function initializeMarketLanguage() {
  const router = qs('[data-market-language-router]');
  if (!router || window.Shopify?.designMode) return;

  const country = String(router.dataset.marketCountry || '').toUpperCase();
  const currentLanguage = String(router.dataset.currentLanguage || '').toLowerCase();
  const targetLanguage = String(router.dataset.marketLanguage || '').toLowerCase();
  const targetAvailable = router.dataset.marketLanguageAvailable === 'true';
  if (!country || !targetLanguage || !targetAvailable || currentLanguage === targetLanguage) return;

  try {
    if (window.localStorage.getItem('maison-lune-language-preference')) return;
    const sessionKey = `maison-lune-market-language:${country}:${targetLanguage}`;
    if (window.sessionStorage.getItem(sessionKey)) return;

    const languageSelector = qs('select[name="language_code"]', router);
    if (!languageSelector || !Array.from(languageSelector.options).some((option) => option.value.toLowerCase() === targetLanguage)) return;

    window.sessionStorage.setItem(sessionKey, '1');
    languageSelector.value = targetLanguage;
    if (typeof languageSelector.form?.requestSubmit === 'function') languageSelector.form.requestSubmit();
    else languageSelector.form?.submit();
  } catch (error) {
    // Keep the storefront usable when browser storage is blocked.
  }
}

function updateStickyAddToCart(variant, selectedOptions = []) {
  const bar = qs('[data-sticky-atc]');
  if (!bar) return;

  const price = qs('[data-sticky-price]', bar);
  const variantLabel = qs('[data-sticky-variant]', bar);
  const button = qs('[data-sticky-add-button]', bar);
  const buttonLabel = qs('[data-sticky-add-label]', bar);
  const optionLabel = selectedOptions.filter(Boolean).map(localizeOptionValue).join(' / ');

  if (price && variant && typeof variant.price !== 'undefined') {
    price.textContent = formatMoney(variant.price);
  }
  if (variantLabel) {
    variantLabel.textContent = optionLabel || '';
    variantLabel.hidden = optionLabel.length === 0;
  }
  if (button) {
    const available = variant ? Boolean(variant.available) : !button.disabled;
    button.disabled = !available;
    if (buttonLabel) {
      buttonLabel.textContent = available
        ? button.dataset.addText
        : button.dataset.soldOutText;
    }
  }
}

function initializeProductGallery() {
  const gallery = qs('[data-product-gallery]');
  if (!gallery) return;
  syncProductGalleryLayout(gallery);
}

function selectProductVariantMedia(form, variant) {
  if (!form || !variant) return;
  const mapNode = qs('[data-product-variant-media]', form);
  const gallery = qs('[data-product-gallery]');
  if (!mapNode || !gallery) return;

  let mediaMap = {};
  try {
    mediaMap = JSON.parse(mapNode.textContent);
  } catch (error) {
    return;
  }

  const mediaId = mediaMap[String(variant.id)];
  if (!mediaId) return;
  const slides = qsa('.product__media-slide', gallery);
  const targetSlide = slides.find((slide) => String(slide.dataset.mediaId) === String(mediaId));
  if (!targetSlide) return;

  const snapPoints = getProductGallerySnapPoints(gallery);
  const maxScroll = Math.max(0, gallery.scrollWidth - gallery.clientWidth);
  const targetLeft = Math.min(targetSlide.offsetLeft, maxScroll);
  const targetIndex = snapPoints.reduce((closestIndex, point, index) => (
    Math.abs(point - targetLeft) < Math.abs(snapPoints[closestIndex] - targetLeft) ? index : closestIndex
  ), 0);
  setProductGallerySlide(gallery, targetIndex);
}

function getProductZoomItems() {
  const gallery = qs('[data-product-gallery]');
  if (!gallery) return [];

  return qsa('[data-product-zoom-src]', gallery)
    .map((slide) => ({
      src: slide.dataset.productZoomSrc,
      alt: slide.dataset.productZoomAlt || '',
      slideIndex: qsa('.product__media-slide', gallery).indexOf(slide),
    }))
    .filter((item) => item.src);
}

function openProductZoom() {
  const modal = qs('[data-product-zoom]');
  const gallery = qs('[data-product-gallery]');
  const items = getProductZoomItems();
  if (!modal || !gallery || items.length === 0) return;

  const currentSlide = Number(gallery.dataset.currentSlide || 0);
  let imageIndex = items.findIndex((item) => item.slideIndex === currentSlide);
  if (imageIndex < 0) {
    imageIndex = items.findIndex((item) => item.slideIndex > currentSlide);
  }
  if (imageIndex < 0) imageIndex = 0;

  setProductZoomImage(imageIndex);
  closeOtherOverlays(modal);
  modal.classList.add('is-open');
  modal.setAttribute('aria-hidden', 'false');
  document.body.classList.add('product-zoom-open');
  syncOverlayLock();
}

function closeProductZoom() {
  const modal = qs('[data-product-zoom]');
  if (!modal) return;

  modal.classList.remove('is-open', 'is-magnified');
  modal.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('product-zoom-open');
  syncOverlayLock();
}

function setProductZoomImage(index) {
  const modal = qs('[data-product-zoom]');
  const items = getProductZoomItems();
  if (!modal || items.length === 0) return;

  const image = qs('[data-product-zoom-image]', modal);
  if (!image) return;

  const safeIndex = Math.max(0, Math.min(index, items.length - 1));
  const item = items[safeIndex];
  modal.dataset.zoomIndex = String(safeIndex);
  modal.classList.remove('is-magnified');
  image.style.removeProperty('--zoom-x');
  image.style.removeProperty('--zoom-y');

  if (image.getAttribute('src') !== item.src) {
    image.setAttribute('src', item.src);
  }
  image.setAttribute('alt', item.alt);

  [items[safeIndex - 1], items[safeIndex + 1]].forEach((nearbyItem) => {
    if (!nearbyItem?.src) return;
    const preload = new Image();
    preload.decoding = 'async';
    preload.src = nearbyItem.src;
  });

  qs('[data-product-zoom-slide="prev"]', modal)?.classList.toggle('is-hidden', safeIndex === 0);
  qs('[data-product-zoom-slide="next"]', modal)?.classList.toggle('is-hidden', safeIndex >= items.length - 1);
}

function endProductGalleryDrag(gallery, pointerId) {
  const wasDragged = gallery.dataset.wasDragged === 'true';
  try {
    gallery.releasePointerCapture?.(pointerId);
  } catch (error) {
    // Pointer capture can already be released by the browser on fast touch gestures.
  }
  delete gallery.dataset.pointerActive;
  delete gallery.dataset.pointerStartX;
  delete gallery.dataset.pointerStartY;
  delete gallery.dataset.pointerScrollLeft;
  delete gallery.dataset.pointerId;

  if (!wasDragged) {
    gallery.classList.remove('is-dragging');
    return;
  }

  const index = getClosestProductGalleryIndex(gallery);
  gallery.classList.remove('is-dragging');
  setProductGallerySlide(gallery, index);

  window.setTimeout(() => {
    if (gallery.dataset.wasDragged === 'true') delete gallery.dataset.wasDragged;
  }, 260);
}

function setProductGallerySlide(gallery, index) {
  const snapPoints = getProductGallerySnapPoints(gallery);
  const maxIndex = Math.max(0, snapPoints.length - 1);
  const safeIndex = Math.max(0, Math.min(index, maxIndex));
  const targetLeft = snapPoints[safeIndex] || 0;

  gallery.dataset.currentSlide = String(safeIndex);
  gallery.scrollTo({ left: targetLeft, behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth' });
  updateProductGalleryControls(gallery, safeIndex);
}

function syncProductGalleryLayout(gallery) {
  if (!gallery) return;
  const snapPoints = getProductGallerySnapPoints(gallery);
  const maxIndex = Math.max(0, snapPoints.length - 1);
  const index = Math.max(0, Math.min(Number(gallery.dataset.currentSlide || 0), maxIndex));
  gallery.scrollLeft = snapPoints[index] || 0;
  updateProductGalleryControls(gallery, index);
}

function updateProductGalleryControls(gallery, index) {
  const snapPoints = getProductGallerySnapPoints(gallery);
  const maxIndex = Math.max(0, snapPoints.length - 1);
  const safeIndex = Math.max(0, Math.min(index, maxIndex));
  const shell = gallery.closest('.product__media-shell');
  const progress = qs('[data-product-gallery-progress]');
  if (!shell) return;

  gallery.dataset.currentSlide = String(safeIndex);
  qs('[data-product-gallery-slide="prev"]', shell)?.classList.toggle('is-hidden', safeIndex === 0);
  qs('[data-product-gallery-slide="next"]', shell)?.classList.toggle('is-hidden', safeIndex >= maxIndex);
  if (progress) progress.style.width = `${((safeIndex + 1) / (maxIndex + 1)) * 100}%`;
}

function getProductGallerySnapPoints(gallery) {
  if (!gallery) return [0];
  const maxScroll = Math.max(0, gallery.scrollWidth - gallery.clientWidth);
  const slides = qsa('.product__media-slide', gallery);
  const cacheKey = `${gallery.scrollWidth}:${gallery.clientWidth}:${slides.length}`;
  const cached = gallerySnapPointCache.get(gallery);
  if (cached?.key === cacheKey) return cached.points;

  const points = slides
    .map((slide) => Math.min(slide.offsetLeft, maxScroll))
    .filter((point, index, values) => index === 0 || Math.abs(point - values[index - 1]) > 2);

  if (points.length === 0) {
    gallerySnapPointCache.set(gallery, { key: cacheKey, points: [0] });
    return [0];
  }
  if (Math.abs(points[points.length - 1] - maxScroll) > 2) points.push(maxScroll);
  gallerySnapPointCache.set(gallery, { key: cacheKey, points });
  return points;
}

function getClosestProductGalleryIndex(gallery) {
  const points = getProductGallerySnapPoints(gallery);
  return points.reduce((closestIndex, point, index) => (
    Math.abs(point - gallery.scrollLeft) < Math.abs(points[closestIndex] - gallery.scrollLeft)
      ? index
      : closestIndex
  ), 0);
}

function setProductCardSlide(slider, index) {
  const count = Number(slider.dataset.slideCount || 1);
  const safeIndex = Math.max(0, Math.min(index, count - 1));
  const track = qs('[data-product-card-track]', slider);
  const wrap = slider.closest('.product-card__media-wrap');
  if (!track || !wrap) return;

  slider.dataset.currentSlide = String(safeIndex);
  if (isMobileViewport()) {
    slider.scrollTo({ left: safeIndex * slider.clientWidth, behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth' });
  } else {
    track.style.transform = `translateX(${-safeIndex * 100}%)`;
  }

  updateProductCardSliderControls(slider, safeIndex);
}

function syncProductCardSliderLayout(slider) {
  if (!slider) return;
  const index = Number(slider.dataset.currentSlide || 0);
  const track = qs('[data-product-card-track]', slider);
  if (!track) return;

  if (isMobileViewport()) {
    track.style.removeProperty('transform');
    slider.scrollLeft = index * slider.clientWidth;
  } else {
    slider.scrollLeft = 0;
    track.style.transform = `translateX(${-index * 100}%)`;
  }

  updateProductCardSliderControls(slider, index);
}

function updateProductCardSliderControls(slider, index) {
  const count = Number(slider.dataset.slideCount || 1);
  const safeIndex = Math.max(0, Math.min(index, count - 1));
  const wrap = slider.closest('.product-card__media-wrap');
  if (!wrap) return;

  slider.dataset.currentSlide = String(safeIndex);
  const previous = qs('[data-product-card-slide="prev"]', wrap);
  const next = qs('[data-product-card-slide="next"]', wrap);
  previous?.classList.toggle('is-hidden', safeIndex === 0);
  next?.classList.toggle('is-hidden', safeIndex >= count - 1);
}

function isMobileViewport() {
  return window.matchMedia('(max-width: 720px)').matches;
}

function isHeaderAutoHideTemplate() {
  return document.body.classList.contains('template-product') || document.body.classList.contains('template-index');
}

function syncOverlayLock() {
  const hasOpenOverlay = Boolean(qs(
    '.nav-drawer.is-open, .search-drawer.is-open, .filter-drawer.is-open, .cart-drawer.is-open, .wishlist-drawer.is-open, .upsell-drawer.is-open, .quick-add-drawer.is-open, .product-zoom.is-open'
  ));
  document.body.classList.toggle('overlay-open', hasOpenOverlay);
  document.body.classList.toggle('cart-drawer-open', Boolean(qs('.cart-drawer.is-open')));
}

function closeOtherOverlays(activeOverlay) {
  qsa('[data-menu-drawer], [data-search-drawer], [data-filter-drawer], [data-cart-drawer], [data-wishlist-drawer], [data-upsell-drawer], [data-quick-add-drawer], [data-product-zoom]').forEach((overlay) => {
    if (overlay === activeOverlay) return;
    overlay.classList.remove('is-open', 'is-magnified');
    overlay.setAttribute('aria-hidden', 'true');
  });

  if (activeOverlay !== qs('[data-product-zoom]')) {
    document.body.classList.remove('product-zoom-open');
  }

  const menuIsActive = activeOverlay === qs('[data-menu-drawer]');
  qs('[data-header]')?.classList.toggle('is-open', menuIsActive);
  qsa('[data-menu-toggle]').forEach((button) => {
    button.setAttribute('aria-expanded', String(menuIsActive));
  });
}

function syncScrollHeaderState() {
  if (!isHeaderAutoHideTemplate()) return;
  const announcement = qs('[data-announcement]');
  const header = qs('[data-header]');
  const headerStackHeight = (announcement?.offsetHeight || 0) + (header?.offsetHeight || 0);
  const threshold = Math.max(48, headerStackHeight - 2);
  document.body.classList.toggle('scroll-header-hidden', window.scrollY > threshold);
}

function setButtonLoading(button, loading = true) {
  if (!button) return;
  button.classList.toggle('is-loading', loading);
  if (loading) {
    button.classList.remove('is-added', 'is-error');
    button.setAttribute('aria-busy', 'true');
  } else {
    button.removeAttribute('aria-busy');
  }
}

function markButtonSuccess(button) {
  if (!button) return;
  button.classList.remove('is-loading', 'is-error');
  button.classList.add('is-added');
  window.setTimeout(() => button.classList.remove('is-added'), 950);
}

function markButtonError(button) {
  if (!button) return;
  button.classList.remove('is-loading', 'is-added');
  button.classList.add('is-error');
  window.setTimeout(() => button.classList.remove('is-error'), 900);
}

function pulseButton(button) {
  if (!button) return;
  button.classList.remove('is-feedback');
  void button.offsetWidth;
  button.classList.add('is-feedback');
  window.setTimeout(() => button.classList.remove('is-feedback'), 420);
}

function formatMoney(cents) {
  const amount = Number(cents || 0) / 100;
  return new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'USD',
  }).format(amount);
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
